import { useState, useEffect } from "react";
import {
  Grid,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  FormControl,
  FormControlLabel,
  Button,
  Container,
  Box,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import CustomTextField from "components/mui/text-field";
import { useParams } from "react-router-dom";
import { SaveClientServiceRequest } from "./logic/SaveClientServiceRequest";
import {toast, ToastContainer} from 'react-toastify';

const steps = ["Service Request", "Additional Information"];

const ServiceRequestPage = () => {

  const { token } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const initialdata = {
    propertyType: "Residential", // default
    firstname: "",
    lastname: "",
    companyName: "",
    companyPhone: "",
    companyEmail: "",
    requestTitle: "",
    jobsiteAddress: "",
    phone: "",
    email: "",
    additionalContact: "",
    servicesRequested: "",
    reqDescription: "",
    propertySize: "",
    numberOfProperties: "",
    bestTimeToCall: "",
    specialInstructions: "",
    token: token 
  }

  const [formData, setFormData] = useState(initialdata);  

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {        
        setErrors({});
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [errors]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateFields = () => {
    let tempErrors = {};
    const nameRegex = /^[A-Za-z]+$/;

      if (formData.firstname) {
        if (!nameRegex.test(formData.firstname)) {
          tempErrors.firstname = "Only alphabets are accepted";
        }
      }

      if (!formData.firstname) {
        tempErrors.firstname = "First name is required";
      }

      if (formData.lastname) {
        if (!nameRegex.test(formData.lastname)) {
          tempErrors.lastname = "Only alphabets are accepted";
        }
      }

      if (!formData.lastname) {
        tempErrors.lastname = "Last name is required";
      }

      if (!formData.email) {
        tempErrors.email = "Email is required";
      } else if (!validateEmail(formData.email)) {
        tempErrors.email = "Enter a valid email";
      }    

      if (!formData.companyName && formData.propertyType === "Business") {
        tempErrors.companyName = "Company name is required";
      }

      if (!formData.companyPhone && formData.propertyType === "Business") {
        tempErrors.companyPhone = "Company phone is required";
      }      

      if (!formData?.companyEmail && formData.propertyType === "Business") {
        tempErrors.companyEmail = "Company email is required";
      }else if (!validateEmail(formData.companyEmail) && formData.propertyType === "Business") {
        tempErrors.companyEmail = "Enter a valid company email";
      } 

      if (!formData.jobsiteAddress) {
        tempErrors.jobsiteAddress = "Jobsite address is required";
      }

      if (!formData.phone) {
        tempErrors.phone = "Phone is required";
      }      

      if (!formData.requestTitle) {
        tempErrors.requestTitle = "Request title is required";
      }

      if (!formData.token) {
        tempErrors.token = "Token is required";
      }
    console.log(tempErrors);
    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(initialdata);
    setErrors({});
  };

  const handleNext = () => {
    if(activeStep === 0) {
      if(validateFields()) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);      
      } 
    }     
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
      try {
        const response = await SaveClientServiceRequest(formData, toast);
        if (!response.isError) {          
          toast.success(response.message || "Service request submitted successfully!", {
            duration: 3000,
            position: 'top-right',
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        } else {
          toast.error(response.message || "An error occurred. Please try again.", {
            duration: 5000,
            position: 'top-right',
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });          
        }
      } catch (error) {
        console.error('Network error:', error);
      }finally{
        resetForm();
        setActiveStep(0);
      }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ width: "100%", mb: 4 }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepConnector-line": {
              borderColor: "#eaeaf0",
            },
            "& .Mui-active .MuiStepConnector-line": {
              borderColor: "#4caf50",
            },
            "& .Mui-completed .MuiStepConnector-line": {
              borderColor: "#4caf50",
            },
          }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    "&.Mui-active": {
                      color: "#23A26D",
                    },
                    "&.Mui-completed": {
                      color: "#23A26D",
                    },
                  },
                }}
                sx={{
                  "& .MuiStepLabel-label": {
                    color: activeStep >= index ? "#23A26D" : "rgba(0, 0, 0, 0.6)",
                    fontWeight: activeStep >= index ? "600" : "400",
                    fontSize: "0.875rem",
                  },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Typography variant="h4" sx={{ mb: 4 }}>
        {steps[activeStep]}
      </Typography>

      {activeStep === 0 ? (
        <>
          <Grid container spacing={3} sx={{mt:3}}>
            {/* Property Type - at top */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    fontWeight: 400,
                    fontSize: "0.83rem",
                    color: "text.primary",
                    "& .MuiFormLabel-asterisk": {
                      color: "error.main",
                    },
                    marginBottom: "4px",
                  }}
                >
                  Property Type
                </FormLabel>
                <RadioGroup
                  row
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                >
                  <FormControlLabel
                    value="Residential"
                    control={<Radio color="success" />}
                    label="Residential"
                  />
                  <FormControlLabel
                    value="Business"
                    control={<Radio color="success" />}
                    label="Business"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            {/* Conditional Fields */}
            {formData.propertyType === "Business" && (
              <>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Company Name"
                    name="companyName"
                    placeholder="Enter company name"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    error={!!errors.companyName}
                    helperText={errors.companyName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Company Email"
                    name="companyEmail"
                    placeholder="Enter company Email"
                    value={formData.companyEmail}
                    onChange={handleChange}
                    required
                    error={!!errors.companyEmail}
                    helperText={errors.companyEmail}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    label="Company Phone"
                    name="companyPhone"
                    placeholder="Enter company phone"
                    value={formData.companyPhone}
                    onChange={handleChange}
                    required
                    error={!!errors.companyPhone}
                    helperText={errors.companyPhone}
                  />
                </Grid>
              </>)
            }
            {/* Request Title */}
            <Grid item xs={12} sm={formData.propertyType === "Residential" ? 12 : 6}>
              <CustomTextField
                fullWidth
                label="Request Title"
                name="requestTitle"
                placeholder="Brief title of your request"
                value={formData.requestTitle}
                onChange={handleChange}
                required
                error={!!errors.requestTitle}
                helperText={errors.requestTitle}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} sx={{mt:3, border: "1px solid #e0e0e0", padding: 2, borderRadius: 1, backgroundColor: "#f9f9f9"}}>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="First Name"
                name="firstname"
                placeholder="Your first name"
                value={formData.firstname}
                onChange={handleChange}
                required
                error={!!errors.firstname}
                helperText={errors.firstname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="Last Name"
                name="lastname"
                placeholder="Your last name"
                value={formData.lastname}
                onChange={handleChange}
                required
                error={!!errors.lastname}
                helperText={errors.lastname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label={
                  formData.propertyType === "business" ? "Company Phone" : "Phone"
                }
                name="phone"
                placeholder={"(555) 555-5555"}
                value={formData.phone}
                onChange={handleChange}
                required
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label={"Email"}
                name="email"
                placeholder={"abc@gmail.com"}
                value={formData.email}
                onChange={handleChange}
                required
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3} sx={{mt:3}}>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Jobsite Address"
                name="jobsiteAddress"
                multiline
                rows={2}
                placeholder="Add address where service is needed"
                value={formData.jobsiteAddress}
                onChange={handleChange}
                required
                error={!!errors.jobsiteAddress}
                helperText={errors.jobsiteAddress}
              />
            </Grid>
            {/* <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Additional Contacts"
                name="additionalContact"
                placeholder="Add contacts"
                value={formData.additionalContact}
                onChange={handleChange}
              />
            </Grid> */}
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Services Requested"
                name="servicesRequested"
                placeholder="List specific services you need"
                multiline
                rows={2}
                value={formData.servicesRequested}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                label="Description"
                name="reqDescription"
                placeholder="Add description"
                multiline
                rows={3}
                value={formData.reqDescription}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </>                                           
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                color: "#BDBDBD",
                fontSize: "0.87rem",
                fontWeight: 360,
              }}
            >
              The following fields help us better understand your needs and
              provide more accurate service.
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTextField
              fullWidth
              label="How big is the property?"
              name="propertySize"
              placeholder="Enter property size"
              value={formData.propertySize}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTextField
              fullWidth
              label="How many properties do you have?"
              name="numberOfProperties"
              placeholder="Enter number of properties"
              value={formData.numberOfProperties}
              onChange={handleChange}
            />
          </Grid>

          {/* <Grid item xs={12}>
            <CustomTextField
              fullWidth
              label="When can we call you?"
              name="bestTimeToCall"
              placeholder="Best date/time to reach you"
              value={formData.bestTimeToCall}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              fullWidth
              label="Special Instructions"
              name="specialInstructions"
              placeholder="Any special access requirement or other important notes"
              multiline
              rows={3}
              value={formData.specialInstructions}
              onChange={handleChange}
            />
          </Grid> */}

          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              By submitting this form, you agree to be contacted regarding
              your service request.
              <br />
              We typically respond within 24 hours during business days.
            </Typography>
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        {activeStep !== 0 && (
          <Button onClick={handleBack} sx={{ mr: 2 }}>
            Back
          </Button>
        )}
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" color="main" onClick={handleSubmit}>
            Submit Request
          </Button>
        ) : (
          <Button variant="contained" color="main" onClick={handleNext}>
            Next
          </Button>
        )}
      </Box>
      {/*<ToastContainer />*/}
    </Container>
  );
};

export default ServiceRequestPage;
